import { useState, useEffect, useCallback } from "react";
import { FileText, Download, RefreshCw, Send, Loader, FileDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

function fmtEur(v) {
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(v || 0);
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function FacturenTab({ token }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setInvoices(data.invoices || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const url = `${API}/api/admin/invoices/export/csv`;
    const a = document.createElement("a");
    a.href = url;
    // Voeg auth header toe via fetch + blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const burl = window.URL.createObjectURL(blob);
        a.href = burl;
        a.download = "FitSmith-facturen-export.csv";
        a.click();
        window.URL.revokeObjectURL(burl);
      });
  };

  const downloadPdf = async (invNr) => {
    setDownloadingPdf(invNr);
    try {
      const res = await fetch(`${API}/api/admin/invoices/${invNr}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FitSmith-factuur-${invNr}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloadingPdf(null);
    }
  };

  const sendExample = async () => {
    setSending(true);
    setSentMsg("");
    try {
      const res = await fetch(`${API}/api/admin/invoices/send-example`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSentMsg(data.bericht || "Verstuurd!");
    } catch (e) {
      setSentMsg("Versturen mislukt");
    } finally {
      setSending(false);
      setTimeout(() => setSentMsg(""), 5000);
    }
  };

  const totaalOmzet = invoices.reduce((s, i) => s + (i.totaal_incl_btw || 0), 0);
  const totaalBtw = invoices.reduce((s, i) => s + (i.btw_totaal || 0), 0);

  return (
    <div data-testid="facturen-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-white font-bold text-xl">Facturen</h2>
          <p className="text-zinc-500 text-sm mt-0.5">{invoices.length} facturen · Automatisch gegenereerd bij betaling</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} data-testid="facturen-refresh-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-white border border-white/10 hover:border-white/20 transition-all">
            <RefreshCw size={13} /> Vernieuwen
          </button>
          <button onClick={downloadCsv} data-testid="facturen-csv-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-[#C9A84C]/40 text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-all">
            <FileDown size={13} /> CSV Moneybird
          </button>
          <button onClick={sendExample} disabled={sending} data-testid="facturen-voorbeeld-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
            style={{ background: GOLD, color: "#000" }}>
            {sending ? <Loader size={13} className="animate-spin" /> : <Send size={13} />}
            Stuur voorbeeld
          </button>
        </div>
      </div>

      {sentMsg && (
        <div data-testid="voorbeeld-sent-msg" className="mb-4 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
          {sentMsg}
        </div>
      )}

      {/* KPI's */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: "Totaal facturen", value: invoices.length, suffix: "" },
          { label: "Totale omzet incl BTW", value: fmtEur(totaalOmzet), suffix: "" },
          { label: "Totaal BTW afdragen", value: fmtEur(totaalBtw), suffix: "" },
        ].map(kpi => (
          <div key={kpi.label} className="bg-white/3 border border-white/8 rounded-xl p-4">
            <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wider mb-1">{kpi.label}</p>
            <p className="text-white font-bold text-xl">{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Tabel */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: `${GOLD}30`, borderTopColor: GOLD }} />
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-16 border border-white/6 rounded-xl bg-white/2">
          <FileText size={36} className="mx-auto mb-3 text-zinc-600" />
          <p className="text-zinc-400 font-semibold">Nog geen facturen</p>
          <p className="text-zinc-600 text-sm mt-1">Facturen worden automatisch aangemaakt bij succesvolle Mollie-betalingen.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/8">
          <table className="w-full text-sm" data-testid="facturen-tabel">
            <thead>
              <tr className="border-b border-white/8 bg-white/3">
                <th className="text-left px-4 py-3 text-zinc-500 font-semibold text-xs uppercase tracking-wider">Factuurnr.</th>
                <th className="text-left px-4 py-3 text-zinc-500 font-semibold text-xs uppercase tracking-wider">Klant</th>
                <th className="text-left px-4 py-3 text-zinc-500 font-semibold text-xs uppercase tracking-wider hidden md:table-cell">Product</th>
                <th className="text-left px-4 py-3 text-zinc-500 font-semibold text-xs uppercase tracking-wider hidden sm:table-cell">Datum</th>
                <th className="text-right px-4 py-3 text-zinc-500 font-semibold text-xs uppercase tracking-wider">Bedrag</th>
                <th className="text-right px-4 py-3 text-zinc-500 font-semibold text-xs uppercase tracking-wider">PDF</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.invoice_number} data-testid={`admin-factuur-${inv.invoice_number}`}
                  className="border-b border-white/5 hover:bg-white/2 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-white font-mono font-semibold text-sm">#{inv.invoice_number}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-white font-medium text-sm">{inv.klant?.naam}</p>
                    <p className="text-zinc-500 text-xs">{inv.klant?.email}</p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-zinc-300 text-sm">{inv.product?.omschrijving}</p>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-sm hidden sm:table-cell">{fmt(inv.datum)}</td>
                  <td className="px-4 py-3 text-right">
                    <p className="text-white font-bold">{fmtEur(inv.totaal_incl_btw)}</p>
                    <p className="text-zinc-600 text-xs">incl. {fmtEur(inv.btw_totaal)} BTW</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => downloadPdf(inv.invoice_number)}
                      disabled={downloadingPdf === inv.invoice_number}
                      data-testid={`admin-download-pdf-${inv.invoice_number}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:scale-105 disabled:opacity-50"
                      style={{ background: GOLD, color: "#000" }}>
                      {downloadingPdf === inv.invoice_number
                        ? <Loader size={11} className="animate-spin" />
                        : <Download size={11} />}
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
